import type { JsonResume, LinkedIn, ReactiveResumeV3 } from "@reactive-resume/parser";
// These JSON/LinkedIn parsers are separate, lightweight exports unrelated to
// the pdf submodule that parse-file.ts lazy-loads (which pulls in
// pdfjs-dist); Nx's rule can't distinguish sub-module usage within one
// package. The pdf code still code-splits correctly regardless — verified
// via the built `pdf-*.js` chunk.
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  JsonResumeParser,
  LinkedInParser,
  ReactiveResumeParser,
  ReactiveResumeV3Parser,
} from "@reactive-resume/parser";
import type { ResumeData } from "@reactive-resume/schema";

export enum ImportType {
  "reactive-resume-json" = "reactive-resume-json",
  "reactive-resume-v3-json" = "reactive-resume-v3-json",
  "json-resume-json" = "json-resume-json",
  "linkedin-data-export-zip" = "linkedin-data-export-zip",
  "pdf-file" = "pdf-file",
  "docx-file" = "docx-file",
  "linkedin-url" = "linkedin-url",
}

export const importTypeOptions = [
  { value: ImportType["pdf-file"], label: "pdf-file" },
  { value: ImportType["reactive-resume-json"], label: "apply-edge-json" },
  { value: ImportType["reactive-resume-v3-json"], label: "legacy-v3-json" },
  { value: ImportType["json-resume-json"], label: "json-resume-json" },
  { value: ImportType["linkedin-data-export-zip"], label: "linkedin-data-export-zip" },
  { value: ImportType["docx-file"], label: "docx-file" },
] as const;

type ParsedImportResult = ResumeData | ReactiveResumeV3 | LinkedIn | JsonResume;

export type ValidationResult =
  | {
      isValid: false;
    }
  | {
      isValid: true;
      type: ImportType;
      result: ParsedImportResult;
    };

export const getImportPayload = (
  validationResult: Extract<ValidationResult, { isValid: true }>,
): ResumeData => {
  if (
    validationResult.type === ImportType["pdf-file"] ||
    validationResult.type === ImportType["docx-file"] ||
    validationResult.type === ImportType["linkedin-url"]
  ) {
    return validationResult.result as ResumeData;
  }

  if (validationResult.type === ImportType["reactive-resume-json"]) {
    const parser = new ReactiveResumeParser();
    return parser.convert(validationResult.result as ResumeData);
  }

  if (validationResult.type === ImportType["reactive-resume-v3-json"]) {
    const parser = new ReactiveResumeV3Parser();
    return parser.convert(validationResult.result as ReactiveResumeV3);
  }

  if (validationResult.type === ImportType["json-resume-json"]) {
    const parser = new JsonResumeParser();
    return parser.convert(validationResult.result as JsonResume);
  }

  const parser = new LinkedInParser();
  return parser.convert(validationResult.result as LinkedIn);
};
